import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  TrendingUp, MapPin, BarChart3, DollarSign, Home, Building2,
  Loader2, Download, RefreshCw, ChevronRight, Sparkles
} from 'lucide-react';
import Navbar from '../../components/layout/Navbar';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const MEXICAN_STATES = [
  'Aguascalientes', 'Baja California', 'Baja California Sur', 'Campeche',
  'Chiapas', 'Chihuahua', 'Ciudad de México', 'Coahuila', 'Colima',
  'Durango', 'Estado de México', 'Guanajuato', 'Guerrero', 'Hidalgo',
  'Jalisco', 'Michoacán', 'Morelos', 'Nayarit', 'Nuevo León', 'Oaxaca',
  'Puebla', 'Querétaro', 'Quintana Roo', 'San Luis Potosí', 'Sinaloa',
  'Sonora', 'Tabasco', 'Tamaulipas', 'Tlaxcala', 'Veracruz', 'Yucatán', 'Zacatecas'
];

export default function MarketReportsPage() {
  const [activeTab, setActiveTab] = useState('generate');
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [trendingZones, setTrendingZones] = useState([]);
  
  // Form state
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [propertyType, setPropertyType] = useState('');
  const [operationType, setOperationType] = useState('');
  
  const token = localStorage.getItem('upim_token');

  useEffect(() => {
    loadTrendingZones();
  }, []);

  const loadTrendingZones = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/market-reports/trending-zones`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrendingZones(res.data.trending_zones || []);
    } catch (err) {
      console.error('Error loading trending zones:', err);
    }
  };

  const generateReport = async () => {
    if (!city || !state) {
      alert('Por favor ingresa ciudad y estado');
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/market-reports/generate`, {
        city,
        state,
        property_type: propertyType || null,
        operation_type: operationType || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReport(res.data.report);
    } catch (err) {
      alert('Error al generar reporte');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (!price) return '-';
    return `$${price.toLocaleString('es-MX', { maximumFractionDigits: 0 })}`;
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <TrendingUp className="w-8 h-8 text-indigo-600" />
                Reportes de Mercado
              </h1>
              <p className="text-gray-500 mt-1">Análisis de mercado con IA por zona</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 overflow-x-auto">
            {[
              { id: 'generate', name: 'Generar Reporte', icon: Sparkles },
              { id: 'trending', name: 'Zonas Tendencia', icon: TrendingUp },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.name}
              </button>
            ))}
          </div>

          {activeTab === 'generate' && (
            <div className="grid md:grid-cols-3 gap-6">
              {/* Form */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-4">Parámetros del Reporte</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Estado *
                    </label>
                    <select
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Seleccionar estado</option>
                      {MEXICAN_STATES.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ciudad *
                    </label>
                    <input
                      type="text"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder="Ej: Monterrey"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo de propiedad
                    </label>
                    <select
                      value={propertyType}
                      onChange={(e) => setPropertyType(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Todos</option>
                      <option value="casa">Casa</option>
                      <option value="departamento">Departamento</option>
                      <option value="terreno">Terreno</option>
                      <option value="local_comercial">Local Comercial</option>
                      <option value="oficina">Oficina</option>
                      <option value="bodega">Bodega</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo de operación
                    </label>
                    <select
                      value={operationType}
                      onChange={(e) => setOperationType(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Todos</option>
                      <option value="venta">Venta</option>
                      <option value="renta">Renta</option>
                    </select>
                  </div>
                  
                  <button
                    onClick={generateReport}
                    disabled={loading || !city || !state}
                    className="w-full flex items-center justify-center gap-2 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Sparkles className="w-5 h-5" />
                    )}
                    Generar Reporte con IA
                  </button>
                </div>
              </div>

              {/* Report Content */}
              <div className="md:col-span-2">
                {report ? (
                  <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="p-6 border-b bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                      <h2 className="text-xl font-bold">{report.title}</h2>
                      <p className="text-indigo-200 text-sm mt-1">
                        Generado: {new Date(report.generated_at).toLocaleString('es-MX')}
                      </p>
                    </div>
                    
                    {/* Stats */}
                    {report.statistics && report.statistics.total_properties > 0 && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6 bg-gray-50">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-indigo-600">
                            {report.statistics.total_properties}
                          </p>
                          <p className="text-xs text-gray-500">Propiedades</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-green-600">
                            {formatPrice(report.statistics.avg_price)}
                          </p>
                          <p className="text-xs text-gray-500">Precio Promedio</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-gray-700">
                            {report.statistics.avg_area?.toFixed(0) || '-'} m²
                          </p>
                          <p className="text-xs text-gray-500">Área Promedio</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-purple-600">
                            {formatPrice(report.statistics.price_per_m2)}/m²
                          </p>
                          <p className="text-xs text-gray-500">Precio por m²</p>
                        </div>
                      </div>
                    )}
                    
                    {/* AI Content */}
                    <div className="p-6 prose prose-sm max-w-none">
                      <div 
                        className="whitespace-pre-wrap text-gray-700 leading-relaxed"
                        dangerouslySetInnerHTML={{ 
                          __html: report.content
                            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                            .replace(/\n\n/g, '</p><p>')
                            .replace(/\n/g, '<br/>')
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-xl p-12 shadow-sm text-center">
                    <BarChart3 className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <h3 className="text-xl font-semibold text-gray-700 mb-2">
                      Genera tu primer reporte
                    </h3>
                    <p className="text-gray-500">
                      Selecciona una zona y genera un análisis de mercado con IA
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'trending' && (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-6 border-b">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">Zonas con Mayor Actividad</h3>
                  <button
                    onClick={loadTrendingZones}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                  >
                    <RefreshCw className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
              </div>
              
              {trendingZones.length === 0 ? (
                <div className="p-12 text-center">
                  <MapPin className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No hay datos de zonas aún</p>
                </div>
              ) : (
                <div className="divide-y">
                  {trendingZones.map((zone, index) => (
                    <div key={index} className="p-4 hover:bg-gray-50 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                          index === 0 ? 'bg-amber-500' :
                          index === 1 ? 'bg-gray-400' :
                          index === 2 ? 'bg-amber-700' :
                          'bg-indigo-600'
                        }`}>
                          {index + 1}
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">{zone.city}</h4>
                          <p className="text-sm text-gray-500">{zone.state}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">
                          {zone.listings_count} propiedades
                        </p>
                        <p className="text-sm text-gray-500">
                          Promedio: {formatPrice(zone.avg_price)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Info */}
          <div className="mt-8 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-6 text-white">
            <div className="flex items-start gap-4">
              <Sparkles className="w-10 h-10 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-bold mb-2">Reportes con Inteligencia Artificial</h3>
                <p className="text-indigo-100 text-sm leading-relaxed">
                  Nuestros reportes utilizan IA para analizar los datos del mercado y generar 
                  insights personalizados para cada zona. Obtén recomendaciones de pricing, 
                  análisis de oferta y demanda, y estrategias para captar clientes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
